import type {
  InsurancePlanOption,
  InsuranceProviderOption,
} from "@shared/types/User";
import type {
  ProviderPortalDashboard,
  ProviderPortalAppointmentsCalendarResult,
  ProviderPortalGeneralSettings,
  ProviderPortalLead,
  ProviderPortalLeadsResult,
  ProviderPortalPatientDetail,
  ProviderPortalProfileSettings,
  ProviderPortalScheduleSettings,
  ProviderPortalInsuranceSettings,
  ProviderPortalSpecialtySettings,
  UpsertProviderPortalAddressInput,
  LeadDocumentItem,
  UpdateProviderPortalGeneralSettingsInput,
  UpdateProviderPortalProfileInput,
  ProviderAddress,
  ProviderPortalAssignedSpecialty,
  ProviderPortalSpecialtyOption,
  ProviderSupportedInsuranceOption,
  ProviderAvailability,
  ProviderLanguage,
  ProviderPortalStaffMember,
  ManagedProviderSummary,
  ProviderRelationship,
  ProviderRelationshipStatus,
  ProviderRelationshipType,
  ProviderRelationshipInheritanceSettings,
} from "@shared/types/Provider";
import type {
  ProviderSearchAnalyticsReport,
  SearchAnalyticsPeriod,
} from "@shared/types/SearchAnalytics";
import apiClient from "@/shared/api/apiClient";
import { API_ROUTES } from "@/constants/apiRoutes";
import { axiosInstance } from "@/shared/api/axios";

function normalizeOptionalProviderId(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export type ProviderLeadScoringRuleItem = {
  ruleCode: string;
  label: string;
  description: string | null;
  dimensionKey: string;
  ruleType: string;
  ruleGroupKey: string | null;
  sortOrder: number;
  masterRuleWeight: number;
  effectiveRuleWeight: number;
  providerOverrideWeight: number | null;
  canEdit: boolean;
};

export type ProviderLeadScoringConfig = {
  provider: {
    providerId: string;
    role: string;
  };
  settings: {
    master: {
      highThreshold: number;
      mediumThreshold: number;
      maxScore: number;
      dimensionWeightsJson: Record<string, number>;
    };
    effective: {
      highThreshold: number;
      mediumThreshold: number;
      maxScore: number;
      dimensionWeightsJson: Record<string, number>;
    };
    providerOverride: {
      highThreshold: number;
      mediumThreshold: number;
      maxScore: number;
      dimensionWeightsJson: Record<string, number>;
    } | null;
    canEdit: boolean;
  };
  rules: ProviderLeadScoringRuleItem[];
  supportMessage: string | null;
  isReadOnly: boolean;
};

export type SearchAnalyticsPeriodOption = {
  value: SearchAnalyticsPeriod;
  label: string;
};

export type ProviderPortalWizardSettings = {
  provider: {
    providerId: string;
    fullName: string;
    providerType: "individual" | "facility";
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
    organizationName: string | null;
    credential: string | null;
    email: string | null;
    primaryPhone: string | null;
    npiNumber: string | null;
    gender: "male" | "female" | "other" | "unknown" | null;
    avatarUrl: string | null;
    websiteUrl: string | null;
    yearsOfExperience: number | null;
    bio: string | null;
    acceptsNewPatients: boolean;
    authorizeInsuranceValidationOnMyBehalf: boolean;
    allowBooking: boolean;
    allowMessaging: boolean;
    authorizedOfficialName: string | null;
    authorizedOfficialTitle: string | null;
    authorizedOfficialPhone: string | null;
  };
  addresses: ProviderAddress[];
  phones: Array<{
    id: string;
    phoneType: "office" | "mobile" | "fax" | "billing" | "other";
    phoneNumber: string;
    extension: string | null;
    isPrimary: boolean;
  }>;
  education: Array<{
    id: string;
    institutionName: string;
    degree: string | null;
    fieldOfStudy: string | null;
    startYear: number | null;
    endYear: number | null;
    description: string | null;
  }>;
  specialties: {
    assignedSpecialties: ProviderPortalAssignedSpecialty[];
    availableSpecialties: ProviderPortalSpecialtyOption[];
  };
  insurances: {
    assignedInsurances: ProviderSupportedInsuranceOption[];
    availableInsurances: ProviderSupportedInsuranceOption[];
  };
  languages: {
    assignedLanguages: Array<ProviderLanguage & { isPrimary: boolean }>;
    availableLanguages: ProviderLanguage[];
  };
  schedule: {
    providerAvailability: ProviderAvailability[];
    staff: ProviderPortalStaffMember[];
  };
  communicationEmails: Array<{
    id: string;
    providerAddressId: string | null;
    email: string;
    purpose: "lead" | "booking_request" | "appointment_request" | "appointment_confirmed" | "general";
    channel: "email";
    isPrimary: boolean;
    isActive: boolean;
    displayName: string | null;
  }>;
};

export type ProviderPortalWizardSaveInput = {
  provider: ProviderPortalWizardSettings["provider"];
  addresses: Array<{
    id?: string | null;
    addressType: "practice" | "mailing" | "billing" | "other";
    line1: string;
    line2?: string | null;
    city: string;
    state: string;
    zipCode?: string | null;
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    isPrimary?: boolean;
  }>;
  phones: Array<{
    phoneType: "office" | "mobile" | "fax" | "billing" | "other";
    phoneNumber: string;
    extension?: string | null;
    isPrimary?: boolean;
  }>;
  education: Array<{
    institutionName: string;
    degree?: string | null;
    fieldOfStudy?: string | null;
    startYear?: number | null;
    endYear?: number | null;
    description?: string | null;
  }>;
  specialtyIds: string[];
  insuranceProviderIds: string[];
  languageIds: string[];
  availability: Array<{
    id?: string | null;
    addressId?: string | null;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    slotDurationMinutes: number;
    isActive?: boolean;
  }>;
  staff: Array<{
    firstName: string;
    lastName: string;
    email: string;
    password?: string | null;
    displayName: string;
    roleName?: string | null;
    addressId?: string | null;
  }>;
  communicationEmails?: Array<{
    id?: string | null;
    providerAddressId?: string | null;
    email: string;
    purpose: "lead" | "booking_request" | "appointment_request" | "appointment_confirmed" | "general";
    channel?: "email";
    isPrimary?: boolean;
    isActive?: boolean;
    displayName?: string | null;
  }>;
};

export type ProviderManagementContext = {
  providerId: string;
  isManagedByParent: boolean;
  parentProviderId: string | null;
  childCanSelfManage: boolean;
  permission: {
    canManage: boolean;
    canViewManagement: boolean;
    canSelfManage: boolean;
    isSelf: boolean;
    isParentManager: boolean;
    relationship: ProviderRelationship | null;
  };
};

export type ProviderScopeMetadata = {
  isParentAccount: boolean;
  parentProviderId: string;
  currentProviderId: string;
  defaultProviderId: null;
  providers: Array<{
    providerId: string;
    providerName: string;
    providerType: "individual" | "facility";
    credential: string | null;
    relationshipType: string | null;
    childCanSelfManage: boolean;
    isParent: boolean;
    isChild: boolean;
    isActive: boolean;
  }>;
};

export type ProviderManagementOverview = {
  parent: {
    providerId: string;
    fullName: string;
    listParentInSearch: boolean;
    specialties: Array<{ id: string; name: string; isPrimary: boolean }>;
    insurances: ProviderSupportedInsuranceOption[];
    googleReview: ProviderGoogleReviewSnapshot | null;
  };
  children: Array<ManagedProviderSummary & {
    ownSpecialties: Array<{ id: string; name: string; isPrimary: boolean }>;
    ownInsurances: ProviderSupportedInsuranceOption[];
    relationshipInheritanceSettings: ProviderRelationshipInheritanceSettings;
    googleReview: ProviderGoogleReviewSnapshot | null;
    effectiveSpecialties: Array<{ id: string; name: string; isPrimary: boolean }>;
    effectiveInsurances: ProviderSupportedInsuranceOption[];
    ownRatingConnected: boolean;
    effectiveRatingSource: "own" | "parent" | "none";
    effectiveGoogleReview: ProviderGoogleReviewSnapshot | null;
    settingsSource: "own" | "inherit";
    resolvedFromParent: boolean;
    allowBooking: boolean;
  }>;
};

export type ChildProviderSubscriptionSummary = {
  childProviderId: string;
  childProviderName?: string;
  planSource: "own_subscription" | "parent_plan" | "none";
  hasOwnActiveSubscription: boolean;
  parentHasActiveSubscription: boolean;
  ownSubscription?: {
    id: string;
    planId?: string | null;
    planName?: string | null;
    planCode?: string | null;
    status?: string | null;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    stripeSubscriptionId?: string | null;
    assignedBy?: string | null;
    createdAt?: string | null;
    updatedAt?: string | null;
  } | null;
  effectivePlan?: {
    planId?: string | null;
    planName?: string | null;
    planCode?: string | null;
    source: "own_subscription" | "parent_plan" | "none";
  } | null;
};

export type ProviderGoogleReviewSnapshot = {
  providerId: string;
  placeId: string;
  placeName: string;
  formattedAddress: string;
  rating: number;
  totalReviews: number;
  syncedAt: string;
  nextSyncAt: string;
};

export type ProviderGoogleReviewCandidate = {
  externalId: string;
  displayName: string;
  formattedAddress: string;
  rating: number | null;
  reviewCount: number | null;
  externalUrl: string;
  confidenceScore?: number;
};

export async function getProviderPortalAppointmentsCalendar(input?: {
  startDate?: string;
  endDate?: string;
  providerId?: string;
}): Promise<ProviderPortalAppointmentsCalendarResult> {
  const query = new URLSearchParams();

  if (input?.startDate) query.set("startDate", input.startDate);
  if (input?.endDate) query.set("endDate", input.endDate);
  if (input?.providerId?.trim()) query.set("providerId", input.providerId.trim());

  const suffix = query.toString() ? `?${query.toString()}` : "";

  return apiClient.get<ProviderPortalAppointmentsCalendarResult>(
    `${API_ROUTES.providerPortal.appointments.calendar}${suffix}`,
  );
}

export async function getProviderPortalDashboard(providerId?: string): Promise<ProviderPortalDashboard & Record<string, unknown>> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.get<{ dashboard: ProviderPortalDashboard }>(
    `${API_ROUTES.providerPortal.dashboard}${suffix}`,
  );

  return data.dashboard;
}

export async function getProviderPortalScopeMetadata(): Promise<ProviderScopeMetadata> {
  return apiClient.get<ProviderScopeMetadata>(API_ROUTES.providerPortal.scope);
}

export async function getProviderPortalSearchIntelligence(
  period: SearchAnalyticsPeriod = "this_month",
  zipCode?: string,
  providerId?: string,
): Promise<{
  featureAvailable?: boolean;
  upgradeRequired?: boolean;
  message?: string;
  report?: ProviderSearchAnalyticsReport;
  periodOptions: SearchAnalyticsPeriodOption[];
}> {
  const params = new URLSearchParams({ period });
  if (zipCode?.trim()) {
    params.set("zipCode", zipCode.trim());
  }
  if (providerId?.trim()) {
    params.set("providerId", providerId.trim());
  }
  const data = await apiClient.get<{
    report: ProviderSearchAnalyticsReport;
    periodOptions: SearchAnalyticsPeriodOption[];
  }>(`${API_ROUTES.providerPortal.analytics.searchIntelligence}?${params.toString()}`);

  return data;
}

export async function getProviderPortalPatientDetail(
  patientId: string,
): Promise<ProviderPortalPatientDetail> {
  const data = await apiClient.get<{ patient: ProviderPortalPatientDetail }>(
    API_ROUTES.providerPortal.patients.detail(patientId),
  );

  return data.patient;
}

export async function getProviderPortalLeads(providerId?: string): Promise<ProviderPortalLeadsResult & Record<string, unknown>> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  return apiClient.get<ProviderPortalLeadsResult & Record<string, unknown>>(
    `${API_ROUTES.providerPortal.leads.list}${suffix}`,
  );
}

export async function getProviderPortalLeadDetail(leadId: string): Promise<ProviderPortalLead> {
  const data = await apiClient.get<{ lead: ProviderPortalLead }>(
    API_ROUTES.providerPortal.leads.detail(leadId),
  );

  return data.lead;
}

export async function confirmProviderPortalLead(leadId: string): Promise<{ lead: ProviderPortalLead; message: string }> {
  return apiClient.post<{ lead: ProviderPortalLead; message: string }>(
    API_ROUTES.providerPortal.leads.confirm(leadId),
  );
}

export async function cancelProviderPortalLead(leadId: string): Promise<{ lead: ProviderPortalLead; message: string }> {
  return apiClient.post<{ lead: ProviderPortalLead; message: string }>(
    API_ROUTES.providerPortal.leads.cancel(leadId),
  );
}

export async function getProviderPortalLeadDocuments(leadId: string): Promise<LeadDocumentItem[]> {
  const data = await apiClient.get<{ documents: LeadDocumentItem[] }>(
    API_ROUTES.providerPortal.leads.documents(leadId),
  );

  return data.documents;
}

export async function uploadProviderPortalLeadDocument(input: {
  leadId: string;
  file: File;
}): Promise<{ document: LeadDocumentItem }> {
  const formData = new FormData();
  formData.append("file", input.file);

  const response = await axiosInstance.post<{ document: LeadDocumentItem }>(
    API_ROUTES.providerPortal.leads.documents(input.leadId),
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );

  return response.data;
}

export async function getProviderPortalLeadDocumentAccess(input: {
  leadId: string;
  documentId: string;
  action?: "viewed" | "downloaded";
}): Promise<{ accessUrl: string }> {
  const response = await axiosInstance.get<{ accessUrl: string }>(
    API_ROUTES.providerPortal.leads.documentAccess(input.leadId, input.documentId),
    {
      params: { action: input.action ?? "viewed" },
    },
  );

  return response.data;
}

function getFileNameFromContentDisposition(value: string | undefined) {
  if (!value) return null;

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const quotedMatch = value.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1];

  const plainMatch = value.match(/filename=([^;]+)/i);
  return plainMatch?.[1]?.trim() ?? null;
}

export async function fetchProviderPortalLeadDocumentBlob(input: {
  leadId: string;
  documentId: string;
  action?: "viewed" | "downloaded";
}): Promise<{ blob: Blob; fileName: string }> {
  const response = await axiosInstance.get<Blob>(
    API_ROUTES.providerPortal.leads.documentAccess(input.leadId, input.documentId),
    {
      params: { action: input.action ?? "viewed" },
      responseType: "blob",
    },
  );
  const contentDisposition = String(response.headers["content-disposition"] ?? "");

  return {
    blob: response.data,
    fileName:
      getFileNameFromContentDisposition(contentDisposition) ??
      `lead-document-${input.action === "downloaded" ? "download" : "view"}`,
  };
}

export async function cancelProviderPortalAppointment(
  appointmentId: string,
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.appointments.cancel(appointmentId),
  );
}

export async function approveProviderPortalAppointment(
  appointmentId: string,
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.appointments.approve(appointmentId),
    undefined,
    { timeout: 60000 },
  );
}

export async function denyProviderPortalAppointment(
  appointmentId: string,
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.appointments.deny(appointmentId),
  );
}

export async function messageProviderPortalLead(
  appointmentId: string,
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.appointments.messagePatient(appointmentId),
  );
}

export async function offerProviderPortalLeadTelehealth(
  appointmentId: string,
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.appointments.offerTelehealth(appointmentId),
  );
}

export async function requestProviderPortalLeadCallback(
  appointmentId: string,
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.appointments.requestCallback(appointmentId),
  );
}

export async function getProviderPortalScheduleSettings(): Promise<ProviderPortalScheduleSettings> {
  const data = await apiClient.get<{ settings: ProviderPortalScheduleSettings }>(
    API_ROUTES.providerPortal.settings.schedule,
  );

  return data.settings;
}

export async function getProviderPortalWizardSettings(
  providerId?: string,
): Promise<ProviderPortalWizardSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.get<{ settings: ProviderPortalWizardSettings }>(
    `${API_ROUTES.providerPortal.settings.onboarding}${suffix}`,
  );

  return data.settings;
}

export async function saveProviderPortalWizardSettings(
  input: ProviderPortalWizardSaveInput,
  providerId?: string,
) {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  return apiClient.put<
    { settings: ProviderPortalWizardSettings; message: string },
    ProviderPortalWizardSaveInput
  >(`${API_ROUTES.providerPortal.settings.onboarding}${suffix}`, input);
}

export async function getProviderPortalProfileSettings(
  providerId?: string,
): Promise<{ profile: ProviderPortalProfileSettings; permission?: ProviderManagementContext["permission"] }> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  return apiClient.get<{ profile: ProviderPortalProfileSettings; permission?: ProviderManagementContext["permission"] }>(
    `${API_ROUTES.providerPortal.settings.profile}${suffix}`,
  );
}

export async function getProviderPortalGeneralSettings(providerId?: string): Promise<ProviderPortalGeneralSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.get<{ settings: ProviderPortalGeneralSettings }>(
    `${API_ROUTES.providerPortal.settings.general}${suffix}`,
  );

  return data.settings;
}

export async function getProviderPortalSpecialtySettings(providerId?: string): Promise<ProviderPortalSpecialtySettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.get<{ settings: ProviderPortalSpecialtySettings }>(
    `${API_ROUTES.providerPortal.settings.specialties}${suffix}`,
  );

  return data.settings;
}

export async function addProviderPortalSpecialty(specialtyId: string, providerId?: string): Promise<ProviderPortalSpecialtySettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.post<{ settings: ProviderPortalSpecialtySettings }>(
    `${API_ROUTES.providerPortal.settings.specialties}${suffix}`,
    { specialtyId },
  );

  return data.settings;
}

export async function removeProviderPortalSpecialty(specialtyId: string, providerId?: string): Promise<ProviderPortalSpecialtySettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.delete<{ settings: ProviderPortalSpecialtySettings }>(
    `${API_ROUTES.providerPortal.settings.specialtyItem(specialtyId)}${suffix}`,
  );

  return data.settings;
}

export async function getProviderPortalInsuranceSettings(providerId?: string): Promise<ProviderPortalInsuranceSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.get<{ settings: ProviderPortalInsuranceSettings }>(
    `${API_ROUTES.providerPortal.settings.insurances}${suffix}`,
  );

  return data.settings;
}

export async function listInsuranceProvidersForPortal(): Promise<InsuranceProviderOption[]> {
  const data = await apiClient.get<{ items: InsuranceProviderOption[] }>(
    API_ROUTES.masterdata.insuranceProviders,
  );

  return data.items;
}

export async function listInsurancePlansForProvider(
  insuranceProviderId: string,
): Promise<InsurancePlanOption[]> {
  const data = await apiClient.get<{ items: InsurancePlanOption[] }>(
    API_ROUTES.masterdata.insuranceProviderPlans(insuranceProviderId),
  );

  return data.items;
}

export async function addProviderPortalInsurance(input: {
  insuranceProviderId: string;
  insurancePlanId?: string | null;
  networkStatus?: "in_network" | "out_of_network" | "unknown";
  acceptingNewPatients?: boolean;
  notes?: string | null;
}, providerId?: string): Promise<ProviderPortalInsuranceSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.post<{ settings: ProviderPortalInsuranceSettings }>(
    `${API_ROUTES.providerPortal.settings.insurances}${suffix}`,
    input,
  );

  return data.settings;
}

export async function updateProviderPortalInsurance(input: {
  providerInsurancePlanId: string;
  insuranceProviderId: string;
  insurancePlanId?: string | null;
  networkStatus?: "in_network" | "out_of_network" | "unknown";
  acceptingNewPatients?: boolean;
  notes?: string | null;
}, providerId?: string): Promise<ProviderPortalInsuranceSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.put<{ settings: ProviderPortalInsuranceSettings }>(
    `${API_ROUTES.providerPortal.settings.insuranceRecordItem(input.providerInsurancePlanId)}${suffix}`,
    {
      insuranceProviderId: input.insuranceProviderId,
      insurancePlanId: input.insurancePlanId ?? null,
      networkStatus: input.networkStatus,
      acceptingNewPatients: input.acceptingNewPatients,
      notes: input.notes ?? null,
    },
  );

  return data.settings;
}

export async function removeProviderPortalInsurance(insuranceProviderId: string, providerId?: string): Promise<ProviderPortalInsuranceSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.delete<{ settings: ProviderPortalInsuranceSettings }>(
    `${API_ROUTES.providerPortal.settings.insuranceItem(insuranceProviderId)}${suffix}`,
  );

  return data.settings;
}

export async function removeProviderPortalInsuranceRecord(
  providerInsurancePlanId: string,
  providerId?: string,
): Promise<ProviderPortalInsuranceSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.delete<{ settings: ProviderPortalInsuranceSettings }>(
    `${API_ROUTES.providerPortal.settings.insuranceRecordItem(providerInsurancePlanId)}${suffix}`,
  );

  return data.settings;
}

export async function getProviderPortalLeadScoringSettings(): Promise<ProviderLeadScoringConfig> {
  return apiClient.get<ProviderLeadScoringConfig>(
    API_ROUTES.providerPortal.settings.leadScoring,
  );
}

export async function updateProviderPortalLeadScoringSettings(input: {
  settings: {
    highThreshold: number;
    mediumThreshold: number;
    maxScore: number;
    dimensionWeightsJson: Record<string, number>;
  };
  rules: Array<{
    ruleCode: string;
    ruleWeight: number;
  }>;
}) {
  return apiClient.put<ProviderLeadScoringConfig & { message: string }>(
    API_ROUTES.providerPortal.settings.leadScoring,
    input,
  );
}

export async function updateProviderPortalProfileSettings(
  input: UpdateProviderPortalProfileInput,
  providerId?: string,
): Promise<ProviderPortalProfileSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.put<
    { profile: ProviderPortalProfileSettings },
    UpdateProviderPortalProfileInput
  >(`${API_ROUTES.providerPortal.settings.profile}${suffix}`, input);

  return data.profile;
}

export async function uploadProviderPortalProfilePhoto(input: { file: File }, providerId?: string) {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const formData = new FormData();
  formData.append("file", input.file);
  const response = await axiosInstance.post<{
    profile: ProviderPortalProfileSettings;
    message?: string;
  }>(`${API_ROUTES.providerPortal.settings.profilePhoto}${suffix}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
}

export async function fetchProviderPortalProfilePhotoBlob(
  providerId?: string,
): Promise<Blob> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const response = await axiosInstance.get<Blob>(
    `${API_ROUTES.providerPortal.settings.profilePhoto}/content${suffix}`,
    {
      responseType: "blob",
    },
  );
  return response.data;
}

export async function fetchProviderPortalProfilePhotoBlobFromUrl(url: string): Promise<Blob> {
  const response = await axiosInstance.get<Blob>(url, {
    responseType: "blob",
  });
  return response.data;
}

export async function getProviderGoogleReviewSettings(providerId?: string): Promise<{
  googleReview: ProviderGoogleReviewSnapshot | null;
}> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  return apiClient.get<{ googleReview: ProviderGoogleReviewSnapshot | null }>(
    `${API_ROUTES.providerPortal.settings.googleReviews}${suffix}`,
  );
}

export async function connectProviderGoogleReviews(input: {
  placeId?: string;
  query?: string;
}, providerId?: string): Promise<{
  googleReview: ProviderGoogleReviewSnapshot | null;
  message: string;
  connected?: boolean;
  requiresSelection?: boolean;
  candidates?: ProviderGoogleReviewCandidate[];
  searchQuery?: string;
}> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  return apiClient.post<{
    googleReview: ProviderGoogleReviewSnapshot | null;
    message: string;
    connected?: boolean;
    requiresSelection?: boolean;
    candidates?: ProviderGoogleReviewCandidate[];
    searchQuery?: string;
  }>(
    `${API_ROUTES.providerPortal.settings.connectGoogleReviews}${suffix}`,
    input,
  );
}

export async function getProviderManagementContext(): Promise<ProviderManagementContext> {
  return apiClient.get<ProviderManagementContext>(API_ROUTES.providerPortal.management.context);
}

export async function getManagedProviders(): Promise<ManagedProviderSummary[]> {
  const data = await apiClient.get<{ providers: ManagedProviderSummary[] }>(
    API_ROUTES.providerPortal.management.providers,
  );
  return data.providers;
}

export async function getProviderManagementOverview(): Promise<ProviderManagementOverview> {
  return apiClient.get<ProviderManagementOverview>(API_ROUTES.providerPortal.management.overview);
}

export async function getChildProviderSubscriptionSummary(
  childProviderId: string,
): Promise<ChildProviderSubscriptionSummary> {
  return apiClient.get<ChildProviderSubscriptionSummary>(
    API_ROUTES.providerPortal.management.providerSubscriptionSummary(childProviderId),
  );
}

export async function removeChildProviderSubscription(
  childProviderId: string,
  reason?: string,
): Promise<{
  success: boolean;
  childProviderId: string;
  previousSubscriptionId?: string | null;
  newPlanSource: "parent_plan" | "none";
  message: string;
  blockedByStripe?: boolean;
}> {
  return apiClient.post<{
    success: boolean;
    childProviderId: string;
    previousSubscriptionId?: string | null;
    newPlanSource: "parent_plan" | "none";
    message: string;
    blockedByStripe?: boolean;
  }>(
    API_ROUTES.providerPortal.management.removeChildSubscription(childProviderId),
    reason ? { reason } : {},
  );
}

export async function updateParentSearchVisibility(input: {
  listParentInSearch: boolean;
}): Promise<{ providerId: string; listParentInSearch: boolean; message: string }> {
  return apiClient.put(API_ROUTES.providerPortal.management.parentVisibility, input);
}

export async function updateProviderRelationshipInheritanceSettings(input: {
  childProviderId: string;
  childCanSelfManage?: boolean;
  childrenCanUseParentPlan?: boolean;
  specialtySourceMode: "inherit_parent" | "own_only" | "own_then_parent_fallback";
  insuranceSourceMode: "inherit_parent" | "own_only" | "own_plus_parent";
  ratingSourceMode: "own_then_parent_fallback" | "own_only" | "parent_only";
}): Promise<{ settings: ProviderRelationshipInheritanceSettings; message: string }> {
  return apiClient.put(API_ROUTES.providerPortal.management.providerInheritance, input);
}

export async function searchProvidersForRelationship(query: string) {
  const data = await apiClient.get<{ providers: Array<{ providerId: string; fullName: string; email: string | null; providerType: "individual" | "facility" }> }>(
    `${API_ROUTES.providerPortal.management.searchProviders}?q=${encodeURIComponent(query)}`,
  );
  return data.providers;
}

export async function linkExistingProviderAsChild(input: {
  parentProviderId: string;
  childProviderId: string;
  relationshipType: ProviderRelationshipType;
  childCanSelfManage?: boolean;
  status?: ProviderRelationshipStatus;
}) {
  return apiClient.post<{ relationship: ProviderRelationship; message: string }>(
    API_ROUTES.providerPortal.management.linkExistingProvider,
    input,
  );
}

export async function createChildProviderFromPortal(input: {
  parentProviderId: string;
  relationshipType: ProviderRelationshipType;
  firstName: string;
  lastName: string;
  displayName?: string;
  email: string;
  phone?: string | null;
  npiNumber?: string | null;
  credential?: string | null;
  organizationName?: string | null;
  providerType?: "individual" | "facility";
  gender?: "male" | "female" | "other" | "unknown" | null;
  bio?: string | null;
  createLoginAccount: boolean;
  password?: string | null;
  showChildInSearch: boolean;
  listParentInSearch?: boolean;
  childCanSelfManage?: boolean;
  childrenCanUseParentPlan?: boolean;
  specialtySourceMode?: "inherit_parent" | "own_only" | "own_then_parent_fallback";
  insuranceSourceMode?: "inherit_parent" | "own_only" | "own_plus_parent";
  ratingSourceMode?: "own_then_parent_fallback" | "own_only" | "parent_only";
}) {
  return apiClient.post<{
    providerId: string;
    relationship: ProviderRelationship;
    createdProviderAccount: boolean;
    message: string;
  }>(API_ROUTES.providerPortal.management.createChildProvider, input);
}

export async function unlinkChildProvider(input: {
  childProviderId: string;
}): Promise<{
  relationshipId: string;
  childProviderId: string;
  relationshipStatus: "inactive";
  childProviderIsActive: false;
  message: string;
}> {
  return apiClient.delete<{
    relationshipId: string;
    childProviderId: string;
    relationshipStatus: "inactive";
    childProviderIsActive: false;
    message: string;
  }>(API_ROUTES.providerPortal.management.unlinkChildProvider(input.childProviderId));
}

export async function addProviderPortalAddress(
  input: UpsertProviderPortalAddressInput,
): Promise<ProviderPortalProfileSettings> {
  const data = await apiClient.post<
    { profile: ProviderPortalProfileSettings },
    UpsertProviderPortalAddressInput
  >(API_ROUTES.providerPortal.settings.addresses, input);

  return data.profile;
}

export async function updateProviderPortalAddress(
  addressId: string,
  input: UpsertProviderPortalAddressInput,
): Promise<ProviderPortalProfileSettings> {
  const data = await apiClient.put<
    { profile: ProviderPortalProfileSettings },
    UpsertProviderPortalAddressInput
  >(API_ROUTES.providerPortal.settings.addressItem(addressId), input);

  return data.profile;
}

export async function removeProviderPortalAddress(
  addressId: string,
): Promise<ProviderPortalProfileSettings> {
  const data = await apiClient.delete<{ profile: ProviderPortalProfileSettings }>(
    API_ROUTES.providerPortal.settings.addressItem(addressId),
  );

  return data.profile;
}

export async function updateProviderPortalGeneralSettings(
  input: UpdateProviderPortalGeneralSettingsInput,
  providerId?: string,
): Promise<ProviderPortalGeneralSettings> {
  const normalizedProviderId = normalizeOptionalProviderId(providerId);
  const suffix = normalizedProviderId
    ? `?providerId=${encodeURIComponent(normalizedProviderId)}`
    : "";
  const data = await apiClient.put<
    { settings: ProviderPortalGeneralSettings },
    UpdateProviderPortalGeneralSettingsInput
  >(`${API_ROUTES.providerPortal.settings.general}${suffix}`, input);

  return data.settings;
}

export type ProviderLeadSettings = {
  providerId: string;
  acceptsWhatsappLeads: boolean;
  acceptsBookingRequests: boolean;
  communicationEmails: Array<{
    id: string;
    providerAddressId: string | null;
    email: string;
    purpose: "lead" | "booking_request" | "appointment_request" | "appointment_confirmed" | "general";
    channel: "email";
    isPrimary: boolean;
    isActive: boolean;
    displayName: string | null;
  }>;
};

export async function getProviderLeadSettings(
  providerId: string,
): Promise<ProviderLeadSettings> {
  return apiClient.get<ProviderLeadSettings>(API_ROUTES.providers.leadSettings(providerId));
}

export async function updateProviderLeadSettings(
  providerId: string,
  input: {
    acceptsWhatsappLeads?: boolean;
    acceptsBookingRequests?: boolean;
  },
): Promise<ProviderLeadSettings> {
  const response = await axiosInstance.patch<ProviderLeadSettings>(
    API_ROUTES.providers.leadSettings(providerId),
    input,
  );
  return response.data;
}

export async function createProviderPortalStaff(input: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  displayName: string;
  addressId?: string | null;
}) {
  return apiClient.post<{ message: string }>(API_ROUTES.providerPortal.staff.list, input);
}

export async function createProviderPortalAvailability(input: {
  addressId?: string | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  slotDurationMinutes: number;
  isActive?: boolean;
}) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.availability.provider,
    input,
  );
}

export async function updateProviderPortalAvailability(
  availabilityId: string,
  input: {
    addressId?: string | null;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    slotDurationMinutes: number;
    isActive?: boolean;
  },
) {
  return apiClient.put<{ message: string }>(
    API_ROUTES.providerPortal.availability.providerItem(availabilityId),
    input,
  );
}

export async function deleteProviderPortalAvailability(availabilityId: string) {
  return apiClient.delete<{ message: string }>(
    API_ROUTES.providerPortal.availability.providerItem(availabilityId),
  );
}

export async function createProviderPortalUnavailability(input: {
  addressId?: string | null;
  startDateTime: string;
  endDateTime: string;
  reason?: string | null;
}) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.unavailability.provider,
    input,
  );
}

export async function updateProviderPortalUnavailability(
  recordId: string,
  input: {
    addressId?: string | null;
    startDateTime: string;
    endDateTime: string;
    reason?: string | null;
  },
) {
  return apiClient.put<{ message: string }>(
    API_ROUTES.providerPortal.unavailability.providerItem(recordId),
    input,
  );
}

export async function deleteProviderPortalUnavailability(recordId: string) {
  return apiClient.delete<{ message: string }>(
    API_ROUTES.providerPortal.unavailability.providerItem(recordId),
  );
}

export async function createProviderPortalStaffAvailability(
  staffId: string,
  input: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    slotDurationMinutes: number;
    isActive?: boolean;
  },
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.staff.availability(staffId),
    input,
  );
}

export async function updateProviderPortalStaffAvailability(
  staffId: string,
  availabilityId: string,
  input: {
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    slotDurationMinutes: number;
    isActive?: boolean;
  },
) {
  return apiClient.put<{ message: string }>(
    API_ROUTES.providerPortal.staff.availabilityItem(staffId, availabilityId),
    input,
  );
}

export async function deleteProviderPortalStaffAvailability(
  staffId: string,
  availabilityId: string,
) {
  return apiClient.delete<{ message: string }>(
    API_ROUTES.providerPortal.staff.availabilityItem(staffId, availabilityId),
  );
}

export async function createProviderPortalStaffUnavailability(
  staffId: string,
  input: {
    startDateTime: string;
    endDateTime: string;
    sourceType?: "manual" | "calendar_sync" | "leave" | "blocked";
    externalEventId?: string | null;
    reason?: string | null;
  },
) {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.staff.unavailability(staffId),
    input,
  );
}

export async function updateProviderPortalStaffUnavailability(
  staffId: string,
  recordId: string,
  input: {
    startDateTime: string;
    endDateTime: string;
    sourceType?: "manual" | "calendar_sync" | "leave" | "blocked";
    externalEventId?: string | null;
    reason?: string | null;
  },
) {
  return apiClient.put<{ message: string }>(
    API_ROUTES.providerPortal.staff.unavailabilityItem(staffId, recordId),
    input,
  );
}

export async function deleteProviderPortalStaffUnavailability(
  staffId: string,
  recordId: string,
) {
  return apiClient.delete<{ message: string }>(
    API_ROUTES.providerPortal.staff.unavailabilityItem(staffId, recordId),
  );
}

export async function getProviderApprovalStatus() {
  return apiClient.get<{ status: { onboardingStatus: string; publishStatus: string; adminReviewNote: string | null } | null }>(
    API_ROUTES.providerPortal.settings.approvalStatus,
  );
}

export async function submitProviderProfileForReview() {
  return apiClient.post<{ message: string }>(
    API_ROUTES.providerPortal.settings.submitForReview,
    {},
  );
}
